import React, { useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import StyledNavButton, { NavLink, StyledSvg } from './NavButton.styles';
import Hyphenated from 'react-hyphen';
import { useWeaveThemeContext } from '../../../hocs/ApplicationWrapper/theme/WeaveContext';
import { useAdpAnalytics } from '../../../contexts/AdskDataServiceProvider';
import { removeSoftHyphens } from '../../../utils/adpUtils';

interface Props {
	children?: React.ReactNode;
	icon?: React.ReactNode;
	to: string;
	path?: string | string[];
	altHighlight?: string | string[];
	dataCy?: string;
	disabled?: boolean;
}

export const NavButton = ({
	children,
	icon,
	to,
	path,
	altHighlight,
	dataCy,
	disabled,
}: Props) => {
	const { isWeaveTheme, weaveThemeConsumer } = useWeaveThemeContext();
	const resolvedRoles = weaveThemeConsumer?.resolvedRoles;
	const match = useRouteMatch({ path: path || to, exact: true });
	const matchAlt = useRouteMatch({ path: altHighlight });
	const adp = useAdpAnalytics();

	const childrenName = children?.toString().trim() ?? '';

	const onNavLinkClick = useCallback(async () => {
		const eventName = removeSoftHyphens(childrenName);
		await adp?.track(
			// event type
			'CLICK_COMPONENT',
			// facet
			{
				id: `nav-${eventName}`,
				type: 'command',
				name: 'COMMON.COMMAND_INVOKED',
				attributes: {
					'COMMON.COMMAND_NAME': eventName,
					'COMMON.COMMAND_CONTEXT': 'Ribbon',
				},
			},
		);
	}, [adp, childrenName]);

	const navLink = (
		<NavLink onClick={onNavLinkClick} id={`nav-${childrenName}`} to={to}>
			{
				<StyledSvg
					$pathColor={
						isWeaveTheme &&
						resolvedRoles['colorScheme.icon.default']
					}>
					{icon}
				</StyledSvg>
			}
			{children}
		</NavLink>
	);

	return (
		<StyledNavButton
			role="button"
			aria-disabled={disabled}
			data-cy={dataCy}
			active={!!match || !!matchAlt}
			color={isWeaveTheme && resolvedRoles['colorScheme.text.default']}
			activeBackgroundColor={
				isWeaveTheme &&
				resolvedRoles['button.segmented.active.backgroundColor']
			}
			activeBorderColor={
				isWeaveTheme &&
				resolvedRoles['button.segmented.active.borderColor']
			}>
			<Hyphenated>{navLink}</Hyphenated>
		</StyledNavButton>
	);
};

export default NavButton;
